// CC-351 — public commercial terms application (no login) with PDF fallback.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/terms`;
const EMPTY_REF = { company: "", contact: "", email: "", phone: "" };
const INIT = { company_name: "", legal_name: "", ein: "", address: "", ap_contact_name: "", ap_email: "", ap_phone: "",
  requested_terms: 15, est_annual_spend: "", bank: { name: "", contact: "", phone: "" }, trade_refs: [EMPTY_REF, EMPTY_REF, EMPTY_REF] };
const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => `${x.field}: ${x.message}`).join("; ");
  return d || "Something went wrong. Please try again.";
};
function Honeypot({ value, onChange }) {
  return (
    <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
      <label>Website<input type="text" name="website" tabIndex={-1} autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} data-testid="terms-honeypot-input" /></label>
    </div>
  );
}
export const NET30_NOTICE = "Net 30 is reviewed case by case and is not guaranteed. It is available only to customers with five or more completed jobs and no late payments, and only by written approval of Mr. Electric management.";
function Net30Notice({ testId }) {
  return <p className="mt-1 text-xs text-[#a16207]" data-testid={testId}>{NET30_NOTICE}</p>;
}
const inp = "w-full rounded-md border border-[#cfd4dc] px-3 py-2 text-sm focus:outline-none focus:border-[#0072bc]";

function Field({ label, req, children }) {
  return <label className="block text-xs font-medium text-[#374151]">{label}{req && <span className="text-[#b91c1c]"> *</span>}<div className="mt-1">{children}</div></label>;
}

function WebForm({ slug, onDone }) {
  const [f, setF] = useState(INIT);
  const [hp, setHp] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setBank = (k) => (e) => setF({ ...f, bank: { ...f.bank, [k]: e.target.value } });
  const setRef = (i, k) => (e) => setF({ ...f, trade_refs: f.trade_refs.map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)) });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    const body = { ...f, requested_terms: Number(f.requested_terms), est_annual_spend: f.est_annual_spend === "" ? null : Number(f.est_annual_spend),
      bank: { ...f.bank, contact: f.bank.contact || null }, trade_refs: f.trade_refs.map((r) => ({ ...r, email: r.email || null, phone: r.phone || null })), website: hp };
    try { await axios.post(`${API}/${slug}/apply`, body); onDone(); } catch (x) { setErr(errText(x)); } finally { setBusy(false); }
  };
  const t = (k, label, req = true, type = "text") => (
    <Field label={label} req={req}><input className={inp} type={type} value={f[k]} onChange={set(k)} required={req} data-testid={`terms-${k}-input`} /></Field>
  );
  return (
    <form onSubmit={submit} className="space-y-6 relative" data-testid="terms-web-form">
      <Honeypot value={hp} onChange={setHp} />
      <div className="grid sm:grid-cols-2 gap-4">
        {t("company_name", "Company name")}{t("legal_name", "Legal business name")}
        {t("ein", "EIN")}{t("address", "Business address")}
        {t("ap_contact_name", "Accounts payable contact")}{t("ap_email", "AP email", true, "email")}
        {t("ap_phone", "AP phone", true, "tel")}
        <Field label="Requested terms" req>
          <select className={inp} value={f.requested_terms} onChange={set("requested_terms")} data-testid="terms-requested-terms-select">
            <option value={15}>Net 15</option><option value={30}>Net 30</option>
          </select>
          {Number(f.requested_terms) === 30 && <Net30Notice testId="terms-net30-notice" />}
        </Field>
        {t("est_annual_spend", "Estimated annual spend ($)", false, "number")}
      </div>
      <fieldset className="grid sm:grid-cols-3 gap-4"><legend className="text-sm font-semibold mb-2">Bank</legend>
        <Field label="Bank name" req><input className={inp} value={f.bank.name} onChange={setBank("name")} required data-testid="terms-bank-name-input" /></Field>
        <Field label="Bank contact"><input className={inp} value={f.bank.contact} onChange={setBank("contact")} data-testid="terms-bank-contact-input" /></Field>
        <Field label="Bank phone" req><input className={inp} value={f.bank.phone} onChange={setBank("phone")} required data-testid="terms-bank-phone-input" /></Field>
      </fieldset>
      {f.trade_refs.map((r, i) => (
        <fieldset key={i} className="grid sm:grid-cols-4 gap-3"><legend className="text-sm font-semibold mb-2">Trade reference {i + 1} (email or phone)</legend>
          <Field label="Company" req><input className={inp} value={r.company} onChange={setRef(i, "company")} required data-testid={`terms-ref-${i}-company-input`} /></Field>
          <Field label="Contact" req><input className={inp} value={r.contact} onChange={setRef(i, "contact")} required data-testid={`terms-ref-${i}-contact-input`} /></Field>
          <Field label="Email"><input className={inp} type="email" value={r.email} onChange={setRef(i, "email")} data-testid={`terms-ref-${i}-email-input`} /></Field>
          <Field label="Phone"><input className={inp} value={r.phone} onChange={setRef(i, "phone")} data-testid={`terms-ref-${i}-phone-input`} /></Field>
        </fieldset>
      ))}
      {err && <div className="text-sm text-[#b91c1c]" data-testid="terms-form-error">{err}</div>}
      <button disabled={busy} className="px-5 py-2.5 rounded-full bg-[#0072bc] text-white text-sm font-semibold disabled:opacity-50" data-testid="terms-submit-btn">
        {busy ? "Submitting…" : "Submit application"}
      </button>
    </form>
  );
}

function PdfForm({ slug, onDone }) {
  const [f, setF] = useState({ company_name: "", ap_contact_name: "", ap_email: "", requested_terms: 15 });
  const [file, setFile] = useState(null);
  const [hp, setHp] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("website", hp);
    fd.append("file", file);
    try { await axios.post(`${API}/${slug}/apply-pdf`, fd); onDone(); } catch (x) { setErr(errText(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 relative" data-testid="terms-pdf-form">
      <Honeypot value={hp} onChange={setHp} />
      {[["company_name", "Company name"], ["ap_contact_name", "AP contact"], ["ap_email", "AP email"]].map(([k, l]) => (
        <Field key={k} label={l} req><input className={inp} value={f[k]} required type={k === "ap_email" ? "email" : "text"}
          onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`terms-pdf-${k}-input`} /></Field>
      ))}
      <Field label="Requested terms" req>
        <select className={inp} value={f.requested_terms} onChange={(e) => setF({ ...f, requested_terms: e.target.value })} data-testid="terms-pdf-terms-select">
          <option value={15}>Net 15</option><option value={30}>Net 30</option>
        </select>
        {Number(f.requested_terms) === 30 && <Net30Notice testId="terms-pdf-net30-notice" />}
      </Field>
      <Field label="Completed application (PDF, max 10 MB)" req>
        <input type="file" accept="application/pdf" required onChange={(e) => setFile(e.target.files[0])} data-testid="terms-pdf-file-input" />
      </Field>
      {err && <div className="sm:col-span-2 text-sm text-[#b91c1c]" data-testid="terms-pdf-error">{err}</div>}
      <div className="sm:col-span-2"><button disabled={busy || !file} className="px-5 py-2.5 rounded-full border border-[#0072bc] text-[#0072bc] text-sm font-semibold disabled:opacity-50" data-testid="terms-pdf-submit-btn">{busy ? "Uploading…" : "Upload PDF application"}</button></div>
    </form>
  );
}

export default function PublicTermsApply() {
  const { tenantSlug } = useParams();
  const [tenant, setTenant] = useState(null);
  const [mode, setMode] = useState("web");
  const [done, setDone] = useState(false);
  useEffect(() => { axios.get(`${API}/${tenantSlug}`).then(({ data }) => setTenant(data)).catch(() => setTenant({ missing: true })); }, [tenantSlug]);
  if (tenant?.missing) return <div className="p-10 text-center" data-testid="terms-not-found">This application link isn't valid.</div>;
  return (
    <div className="min-h-screen bg-[#f6f7f9] py-10 px-4">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-[#e5e7eb] p-6 sm:p-10" data-testid="terms-apply-page">
        <h1 className="text-3xl sm:text-4xl font-semibold text-[#0e1420]">Payment terms application</h1>
        <p className="mt-2 text-sm text-[#4b5563]">{tenant?.tenant_name ? `${tenant.tenant_name}. ` : ""}Terms are not guaranteed; we review bank and trade references before deciding.</p>
        {done ? (
          <div className="mt-8 text-base text-[#0e6b52]" data-testid="terms-apply-thanks">Thank you, we received your application. We'll email you with our decision.</div>
        ) : (
          <div className="mt-8 space-y-6">
            <div className="flex gap-2 text-sm">
              <button onClick={() => setMode("web")} className={`px-3 py-1.5 rounded-full ${mode === "web" ? "bg-[#0e1420] text-white" : "border border-[#cfd4dc]"}`} data-testid="terms-mode-web">Fill in online</button>
              <button onClick={() => setMode("pdf")} className={`px-3 py-1.5 rounded-full ${mode === "pdf" ? "bg-[#0e1420] text-white" : "border border-[#cfd4dc]"}`} data-testid="terms-mode-pdf">Upload a PDF instead</button>
            </div>
            {mode === "web" ? <WebForm slug={tenantSlug} onDone={() => setDone(true)} /> : <PdfForm slug={tenantSlug} onDone={() => setDone(true)} />}
          </div>
        )}
      </div>
    </div>
  );
}
